import React, { Component, useState } from 'react';
import { Button, Collapse } from 'reactstrap';
import { logException } from '../services/errorLogger';

// Shared error display UI component
export const ErrorDisplay = ({ error, onRetry, onGoHome }) => {
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [copied, setCopied] = useState(false);

    const toggleDetails = () => setDetailsOpen(prev => !prev);

    const copyToClipboard = () => {
        const text = `Unexpected Application Error!\n${error?.message}\n${error?.stack}`;
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <div className="d-flex align-items-center justify-content-center min-vh-100 bg-broken text-white p-4">
            <div className="text-center" style={{ maxWidth: '900px', width: '100%' }}>
                <span className="fas fa-exclamation-triangle text-warning mb-4" style={{ fontSize: '4rem' }} />
                <h2 className="mb-3">Something went wrong</h2>
                <p className="text-secondary mb-4">
                    An unexpected error occurred. You can try again or return to the homepage.
                </p>
                <div className="d-flex gap-3 justify-content-center mb-4">
                    <Button color="primary" onClick={onRetry}>
                        <span className="fas fa-redo me-2" />
                        Try Again
                    </Button>
                    <Button color="secondary" outline onClick={onGoHome}>
                        <span className="fas fa-home me-2" />
                        Go Home
                    </Button>
                </div>

                <Button
                    color="link"
                    className="text-secondary p-0"
                    onClick={toggleDetails}
                >
                    <span className={`fas fa-chevron-${detailsOpen ? 'up' : 'down'} me-2`} />
                    {detailsOpen ? 'Hide' : 'Show'} error details
                </Button>

                <Collapse isOpen={detailsOpen}>
                    <div
                        className="mt-3 p-3 bg-black rounded text-start position-relative"
                        style={{ maxHeight: '500px', overflow: 'auto' }}
                    >
                        <Button
                            color={copied ? 'success' : 'secondary'}
                            size="sm"
                            className="position-absolute"
                            style={{ top: '8px', right: '8px' }}
                            onClick={copyToClipboard}
                        >
                            <span className={`fas fa-${copied ? 'check' : 'copy'} me-1`} />
                            {copied ? 'Copied!' : 'Copy'}
                        </Button>
                        <p className="text-danger fw-bold mb-2">Unexpected Application Error!</p>
                        <p className="text-warning mb-2">{error?.message}</p>
                        <pre className="text-secondary mb-0 small" style={{ whiteSpace: 'pre-wrap' }}>
                            {error?.stack}
                        </pre>
                    </div>
                </Collapse>
            </div>
        </div>
    );
};

// Class-based ErrorBoundary for React error catching
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        logException(error, errorInfo);
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null });
    };

    handleGoHome = () => {
        this.setState({ hasError: false, error: null });
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            return (
                <ErrorDisplay
                    error={this.state.error}
                    onRetry={this.handleRetry}
                    onGoHome={this.handleGoHome}
                />
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
